import React from 'react';
import { FiSquare } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Polygon, Line } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// Visual Components
const QuadrilateralTypesVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Types of Quadrilaterals
    </h4>
    <Mafs width={500} height={350} viewBox={{ x: [-6, 6], y: [-4, 4] }}>
      <Coordinates.Cartesian />

      {/* Square */}
      <Polygon
        points={[
          [-5, 2],
          [-3, 2],
          [-3, 0],
          [-5, 0],
        ]}
        color="#ef4444"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-4} y={-0.7} color="#dc2626" size={14} weight="bold">
        Square
      </Text>
      <Text x={-4.3} y={-1} color="#1f2937" size={12}>
        All sides equal
      </Text>

      {/* Rectangle */}
      <Polygon
        points={[
          [-1.5, 2],
          [1.5, 2],
          [1.5, 0],
          [-1.5, 0],
        ]}
        color="#10b981"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={0} y={-0.7} color="#059669" size={14} weight="bold">
        Rectangle
      </Text>
      <Text x={-0.5} y={-1} color="#1f2937" size={12}>
        4 right angles
      </Text>

      {/* Rhombus */}
      <Polygon
        points={[
          [3, 1],
          [4, 2],
          [5, 1],
          [4, 0],
        ]}
        color="#2563eb"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={4} y={-0.7} color="#1d4ed8" size={14} weight="bold">
        Rhombus
      </Text>
      <Text x={3.7} y={-1} color="#1f2937" size={12}>
        All sides equal
      </Text>

      {/* Parallelogram */}
      <Polygon
        points={[
          [-5, -1],
          [-3, -1],
          [-2, -3],
          [-4, -3],
        ]}
        color="#f59e0b"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={-3.5} y={-3.7} color="#d97706" size={14} weight="bold">
        Parallelogram
      </Text>
      <Text x={-4} y={-4} color="#1f2937" size={12}>
        Opposite sides parallel
      </Text>

      {/* Trapezoid */}
      <Polygon
        points={[
          [0, -1],
          [2, -1],
          [1.5, -3],
          [-0.5, -3],
        ]}
        color="#7c3aed"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={0.7} y={-3.7} color="#6d28d9" size={14} weight="bold">
        Trapezoid
      </Text>
      <Text x={0.5} y={-4} color="#1f2937" size={12}>
        One pair parallel
      </Text>

      {/* Kite */}
      <Polygon
        points={[
          [4, -1],
          [4.5, -2],
          [4, -3],
          [3.5, -2],
        ]}
        color="#ec4899"
        fillOpacity={0.3}
        strokeWidth={2}
      />
      <Text x={4} y={-3.7} color="#db2777" size={14} weight="bold">
        Kite
      </Text>
      <Text x={3.7} y={-4} color="#1f2937" size={12}>
        Two pairs equal
      </Text>
    </Mafs>
  </div>
);

const RectanglePropertiesVisual = () => {
  const rectPoints = [
    [-2, -1],
    [2, -1],
    [2, 1],
    [-2, 1],
  ];
  const markerSize = 0.2;

  return (
    <div
      style={{
        padding: '1.5rem',
        background: '#fdfcff',
        borderRadius: '16px',
        border: '1px solid #ede7f3',
      }}
    >
      <h4
        style={{
          textAlign: 'center',
          marginBottom: '1.5rem',
          color: '#333',
          fontSize: '1.5rem',
          fontWeight: '500',
        }}
      >
        Rectangle Properties
      </h4>
      <Mafs
        width={400}
        height={300}
        viewBox={{ x: [-3.5, 3.5], y: [-2.5, 2.5] }}
        pan={false}
      >
        <Coordinates.Cartesian
          xAxis={{ lines: false, labels: false }}
          yAxis={{ lines: false, labels: false }}
        />

        {/* Rectangle */}
        <Polygon
          points={rectPoints}
          color="#a9cbf7"
          fillColor="#d9e9fa"
          strokeWidth={1.5}
        />

        {/* Diagonals */}
        <Plot.OfX
          y={(x) => 0.5 * x}
          color="#e15352"
          strokeWidth={2}
          domain={[-3.5, 3.5]}
        />
        <Plot.OfX
          y={(x) => -0.5 * x}
          color="#e15352"
          strokeWidth={2}
          domain={[-3.5, 3.5]}
        />

        {/* Right angle markers */}
        <Polygon
          points={[
            [-2 + markerSize, -1],
            [-2, -1],
            [-2, -1 + markerSize],
          ]}
          color="#36a37a"
          fillColor="#36a37a"
        />
        <Polygon
          points={[
            [2 - markerSize, -1],
            [2, -1],
            [2, -1 + markerSize],
          ]}
          color="#36a37a"
          fillColor="#36a37a"
        />
        <Polygon
          points={[
            [2 - markerSize, 1],
            [2, 1],
            [2, 1 - markerSize],
          ]}
          color="#36a37a"
          fillColor="#36a37a"
        />
        <Polygon
          points={[
            [-2 + markerSize, 1],
            [-2, 1],
            [-2, 1 - markerSize],
          ]}
          color="#36a37a"
          fillColor="#36a37a"
        />

        {/* Labels */}
        <Text x={0} y={-1.4} color="#1d4ed8" size={16} weight="bold">
          length = 4
        </Text>
        <Text x={-3} y={0} color="#1d4ed8" size={16} weight="bold">
          width = 2
        </Text>
        <Text x={0.5} y={0.8} color="#dc2626" size={16} weight="bold">
          Diagonals are equal
        </Text>
        <Text x={-2.5} y={-1.4} color="#059669" size={16} weight="bold">
          90°
        </Text>
      </Mafs>
    </div>
  );
};

const ParallelogramVisual = () => {
  const paraPoints = [
    [-2.5, -1],
    [0.5, -1],
    [2.5, 1],
    [-0.5, 1],
  ];

  return (
    <div
      style={{
        padding: '1.5rem',
        background: '#fdfcff',
        borderRadius: '16px',
        border: '1px solid #ede7f3',
      }}
    >
      <h4
        style={{
          textAlign: 'center',
          marginBottom: '1.5rem',
          color: '#333',
          fontSize: '1.5rem',
          fontWeight: '500',
        }}
      >
        Parallelogram Properties
      </h4>
      <Mafs
        width={400}
        height={300}
        viewBox={{ x: [-3.5, 3.5], y: [-2.5, 2.5] }}
        pan={false}
      >
        <Coordinates.Cartesian
          xAxis={{ lines: false, labels: false }}
          yAxis={{ lines: false, labels: false }}
        />

        {/* Parallelogram */}
        <Polygon
          points={paraPoints}
          color="#f7a950"
          fillColor="#fcead6"
          strokeWidth={2}
        />

        {/* Diagonals */}
        <Line.Segment
          point1={[-2.5, -1]}
          point2={[2.5, 1]}
          color="#7a4ff4"
          strokeWidth={2}
        />
        <Line.Segment
          point1={[0.5, -1]}
          point2={[-0.5, 1]}
          color="#7a4ff4"
          strokeWidth={2}
        />

        {/* Center point */}
        <Point x={0} y={0} color="#7a4ff4" />

        {/* Labels */}
        <Text x={0} y={-1.8} color="#1f2937" size={16} weight="bold">
          Opposite sides parallel & equal
        </Text>
        <Text x={-0.3} y={1.6} color="#6d28d9" size={16} weight="bold">
          Diagonals bisect each other
        </Text>
      </Mafs>
    </div>
  );
};

const TrapezoidVisual = () => (
  <div
    style={{
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid var(--liwaza-outline-variant)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1rem',
        color: 'var(--liwaza-primary)',
      }}
    >
      Trapezoid Area
    </h4>
    <Mafs width={400} height={300} viewBox={{ x: [-3, 3], y: [-2, 2] }}>
      <Coordinates.Cartesian />

      {/* Trapezoid */}
      <Polygon
        points={[
          [-2, -1],
          [2, -1],
          [1, 1],
          [-1, 1],
        ]}
        color="#7c3aed"
        fillOpacity={0.3}
        strokeWidth={2}
      />

      {/* Height line */}
      <Plot.OfY
        x={() => 0}
        color="#ef4444"
        strokeWidth={2}
        strokeDasharray="5,5"
        domain={[-1, 1]}
      />

      {/* Labels */}
      <Text x={0} y={-1.5} color="#1d4ed8" size={14} weight="bold">
        b₁ = 4
      </Text>
      <Text x={0} y={1.5} color="#1d4ed8" size={14} weight="bold">
        b₂ = 2
      </Text>
      <Text x={0.5} y={0} color="#dc2626" size={14} weight="bold">
        h = 2
      </Text>

      {/* Formula */}
      <Text x={0} y={-2} color="#1f2937" size={14} weight="bold">
        Area = ½(b₁ + b₂)h
      </Text>
      <Text x={0} y={-2.3} color="#1f2937" size={12}>
        = ½(4 + 2)(2) = 6
      </Text>
    </Mafs>
  </div>
);

const QuadrilateralsLesson = () => {
  const sections = [
    {
      id: 'quadrilateral-types',
      title: 'Types of Quadrilaterals',
      content: {
        introduction:
          'Quadrilaterals are four-sided polygons with different properties based on their sides and angles.',

        concepts: [
          {
            title: 'Classification of Quadrilaterals',
            explanation:
              'Quadrilaterals include squares, rectangles, rhombuses, parallelograms, trapezoids, and kites, each with unique properties.',
            visual: <QuadrilateralTypesVisual />,
            example: {
              problem:
                'Identify the type of quadrilateral with four equal sides and four right angles.',
              solution: [
                'Four equal sides suggests a rhombus or square',
                'Four right angles suggests a rectangle or square',
                "Having both properties means it's a square",
                "A square is a special case that's both a rhombus and rectangle",
              ],
            },
          },
        ],
      },
    },
    {
      id: 'rectangles-squares',
      title: 'Rectangles & Squares',
      content: {
        introduction:
          'Rectangles have four right angles and opposite sides equal. Squares are special rectangles with all sides equal.',

        concepts: [
          {
            title: 'Rectangle Properties',
            explanation:
              'In rectangles, opposite sides are parallel and equal, all angles are 90°, and diagonals are equal and bisect each other.',
            visual: <RectanglePropertiesVisual />,
            example: {
              problem:
                'A rectangle has length 8 and width 3. Find its area, perimeter, and diagonal length.',
              solution: [
                'Area = §\\text{length} \\times \\text{width} = 8 \\times 3 = 24§',
                'Perimeter = §2(\\text{length} + \\text{width}) = 2(8 + 3) = 22§',
                'Diagonal = §\\sqrt{\\text{length}² + \\text{width}²} = \\sqrt{8² + 3²} = \\sqrt{64 + 9} = \\sqrt{73}§',
                'Diagonal §\\approx 8.54§ units',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'parallelograms',
      title: 'Parallelograms',
      content: {
        introduction:
          'Parallelograms have opposite sides parallel and equal. They include rectangles, rhombuses, and squares as special cases.',

        concepts: [
          {
            title: 'Parallelogram Properties',
            explanation:
              'Opposite sides are parallel and equal, opposite angles are equal, and consecutive angles are supplementary. Diagonals bisect each other.',
            visual: <ParallelogramVisual />,
            example: {
              problem:
                'In a parallelogram, one angle is 120°. Find all four angles.',
              solution: [
                'Let the angles be A, B, C, D in order',
                'Given: A = 120°',
                'Opposite angles are equal: C = A = 120°',
                'Consecutive angles are supplementary: B = 180° - 120° = 60°',
                'D = B = 60°. The angles are 120°, 60°, 120°, 60°',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'trapezoids',
      title: 'Trapezoids',
      content: {
        introduction:
          'Trapezoids have exactly one pair of parallel sides called bases. The area depends on both bases and the height.',

        concepts: [
          {
            title: 'Trapezoid Area Formula',
            explanation:
              'Area = §½(b₁ + b₂)h§, where b₁ and b₂ are the parallel bases and h is the height between them.',
            visual: <TrapezoidVisual />,
            example: {
              problem:
                'A trapezoid has bases of length 12 and 8, and height 5. Find its area.',
              solution: [
                'Use trapezoid area formula: §Area = ½(b₁ + b₂)h§',
                'Identify values: b₁ = 12, b₂ = 8, h = 5',
                'Substitute: §Area = ½(12 + 8)(5) = ½(20)(5)§',
                'Area = 50 square units',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'A rectangle has length 12 and width 5. What is its area?',
      options: ['60', '34', '17', '144', '25'],
      correct: 0,
      explanation:
        'Area of rectangle = §\\text{length} \\times \\text{width} = 12 \\times 5 = 60§',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'In a parallelogram, one angle is 65°. What is the adjacent angle?',
      options: ['115°', '65°', '90°', '125°', '180°'],
      correct: 0,
      explanation:
        'Adjacent angles in a parallelogram are supplementary: §180° - 65° = 115°§',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A trapezoid has bases 6 and 10, and height 4. What is its area?',
      options: ['32', '24', '64', '40', '16'],
      correct: 0,
      explanation:
        'Trapezoid area = §½(b₁ + b₂)h = ½(6 + 10)(4) = ½(16)(4) = 32§',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question: 'A square has perimeter 36. What is its area?',
      options: ['81', '144', '36', '9', '324'],
      correct: 0,
      explanation:
        'Perimeter = §4s = 36§, so side length §s = 9§. Area = §s² = 9² = 81§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'A rhombus has diagonals of length 8 and 6. What is its area?',
      options: ['24', '48', '14', '28', '96'],
      correct: 0,
      explanation:
        'Area of rhombus = §½ \\times d₁ \\times d₂ = ½ \\times 8 \\times 6 = 24§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A rhombus has a perimeter of 40 cm. What is the length of one side?',
      options: ['10 cm', '8 cm', '12 cm', '5 cm', '20 cm'],
      correct: 0,
      explanation:
        'A rhombus has 4 equal sides. Perimeter = §4s = 40§, so §s = 10§ cm.',
    },
    {
      type: 'timed',
      timeLimit: 135,
      question:
        'The diagonals of a rhombus are 16 cm and 12 cm. What is its area?',
      options: ['96 cm²', '192 cm²', '48 cm²', '144 cm²', '72 cm²'],
      correct: 0,
      explanation:
        'Area of rhombus = §½ \\times d₁ \\times d₂ = ½ \\times 16 \\times 12 = 96§ cm².',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'Which quadrilateral has all sides equal but not necessarily all angles equal?',
      options: ['Rhombus', 'Rectangle', 'Square', 'Trapezoid', 'Kite'],
      correct: 0,
      explanation: 'A rhombus has all sides equal but angles may not be 90°',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'How many parallel sides does a trapezoid have?',
      options: [
        'Exactly one pair',
        'Two pairs',
        'No parallel sides',
        'Three sides',
        'All four sides',
      ],
      correct: 0,
      explanation:
        'A trapezoid has exactly one pair of parallel sides (the bases)',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'In a rectangle, the diagonals are:',
      options: [
        'Equal in length and bisect each other',
        "Equal in length but don't bisect each other",
        'Unequal in length but bisect each other',
        "Unequal in length and don't bisect each other",
        'Perpendicular to each other',
      ],
      correct: 0,
      explanation:
        'In a rectangle, diagonals are equal in length and bisect each other at the center.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is the sum of interior angles in any quadrilateral?',
      options: ['360°', '180°', '540°', '720°', 'Depends on the type'],
      correct: 0,
      explanation:
        'The sum of interior angles in any quadrilateral is always 360°.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'A parallelogram with four right angles is called a:',
      options: ['Rectangle', 'Rhombus', 'Square', 'Trapezoid', 'Kite'],
      correct: 0,
      explanation:
        "A parallelogram with four right angles is a rectangle. If it also has equal sides, it's a square.",
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A square has a diagonal of §10\\sqrt{2}§ cm. What is its side length?',
      options: ['10 cm', '§5\\sqrt{2}§ cm', '20 cm', '5 cm', '15 cm'],
      correct: 0,
      explanation:
        'In a square: §d = s\\sqrt{2}§. So §10\\sqrt{2} = s\\sqrt{2}§ → §s = 10§ cm.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A rectangle has a length of 12 cm and width of 5 cm. What is the length of its diagonal?',
      options: ['13 cm', '17 cm', '15 cm', '11 cm', '14 cm'],
      correct: 0,
      explanation:
        'Diagonal = §\\sqrt{l² + w²} = \\sqrt{12² + 5²} = \\sqrt{144 + 25} = \\sqrt{169} = 13§ cm.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'In a trapezoid, the parallel sides are called:',
      options: ['Bases', 'Legs', 'Diagonals', 'Heights', 'Medians'],
      correct: 0,
      explanation:
        'The parallel sides of a trapezoid are called bases (b₁ and b₂). The non-parallel sides are legs.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question: 'A rectangle has length 12 and width 5. What is its area?',
      options: ['60', '34', '17', '144', '25'],
      correct: 0,
      explanation:
        'Area of rectangle = §\\text{length} \\times \\text{width} = 12 \\times 5 = 60§',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'In a parallelogram, one angle is 65°. What is the adjacent angle?',
      options: ['115°', '65°', '90°', '125°', '180°'],
      correct: 0,
      explanation:
        'Adjacent angles in a parallelogram are supplementary: §180° - 65° = 115°§',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A trapezoid has bases 6 and 10, and height 4. What is its area?',
      options: ['32', '24', '64', '40', '16'],
      correct: 0,
      explanation:
        'Trapezoid area = §½(b₁ + b₂)h = ½(6 + 10)(4) = ½(16)(4) = 32§',
    },
    {
      type: 'untimed',
      timeLimit: 95,
      question: 'A square has perimeter 36. What is its area?',
      options: ['81', '144', '36', '9', '324'],
      correct: 0,
      explanation:
        'Perimeter = §4s = 36§, so side length §s = 9§. Area = §s² = 9² = 81§',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question: 'A rhombus has diagonals of length 8 and 6. What is its area?',
      options: ['24', '48', '14', '28', '96'],
      correct: 0,
      explanation:
        'Area of rhombus = §½ \\times d₁ \\times d₂ = ½ \\times 8 \\times 6 = 24§',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'A rhombus has a perimeter of 40 cm. What is the length of one side?',
      options: ['10 cm', '8 cm', '12 cm', '5 cm', '20 cm'],
      correct: 0,
      explanation:
        'A rhombus has 4 equal sides. Perimeter = §4s = 40§, so §s = 10§ cm.',
    },
    {
      type: 'untimed',
      timeLimit: 135,
      question:
        'The diagonals of a rhombus are 16 cm and 12 cm. What is its area?',
      options: ['96 cm²', '192 cm²', '48 cm²', '144 cm²', '72 cm²'],
      correct: 0,
      explanation:
        'Area of rhombus = §½ \\times d₁ \\times d₂ = ½ \\times 16 \\times 12 = 96§ cm².',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'Which quadrilateral has all sides equal but not necessarily all angles equal?',
      options: ['Rhombus', 'Rectangle', 'Square', 'Trapezoid', 'Kite'],
      correct: 0,
      explanation: 'A rhombus has all sides equal but angles may not be 90°',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'How many parallel sides does a trapezoid have?',
      options: [
        'Exactly one pair',
        'Two pairs',
        'No parallel sides',
        'Three sides',
        'All four sides',
      ],
      correct: 0,
      explanation:
        'A trapezoid has exactly one pair of parallel sides (the bases)',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question: 'In a rectangle, the diagonals are:',
      options: [
        'Equal in length and bisect each other',
        "Equal in length but don't bisect each other",
        'Unequal in length but bisect each other',
        "Unequal in length and don't bisect each other",
        'Perpendicular to each other',
      ],
      correct: 0,
      explanation:
        'In a rectangle, diagonals are equal in length and bisect each other at the center.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'What is the sum of interior angles in any quadrilateral?',
      options: ['360°', '180°', '540°', '720°', 'Depends on the type'],
      correct: 0,
      explanation:
        'The sum of interior angles in any quadrilateral is always 360°.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'A parallelogram with four right angles is called a:',
      options: ['Rectangle', 'Rhombus', 'Square', 'Trapezoid', 'Kite'],
      correct: 0,
      explanation:
        "A parallelogram with four right angles is a rectangle. If it also has equal sides, it's a square.",
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A square has a diagonal of §10\\sqrt{2}§ cm. What is its side length?',
      options: ['10 cm', '§5\\sqrt{2}§ cm', '20 cm', '5 cm', '15 cm'],
      correct: 0,
      explanation:
        'In a square: §d = s\\sqrt{2}§. So §10\\sqrt{2} = s\\sqrt{2}§ → §s = 10§ cm.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A rectangle has a length of 12 cm and width of 5 cm. What is the length of its diagonal?',
      options: ['13 cm', '17 cm', '15 cm', '11 cm', '14 cm'],
      correct: 0,
      explanation:
        'Diagonal = §\\sqrt{l² + w²} = \\sqrt{12² + 5²} = \\sqrt{144 + 25} = \\sqrt{169} = 13§ cm.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question: 'In a trapezoid, the parallel sides are called:',
      options: ['Bases', 'Legs', 'Diagonals', 'Heights', 'Medians'],
      correct: 0,
      explanation:
        'The parallel sides of a trapezoid are called bases (b₁ and b₂). The non-parallel sides are legs.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'A rectangle is 8 by 5. Compare the two quantities.',
      quantityA: 'Area',
      quantityB: 'Perimeter',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which quadrilaterals always have opposite sides parallel? Select all that apply.',
      options: ['Parallelogram', 'Rectangle', 'Square', 'Trapezoid', 'Rhombus'],
      correct: [0, 1, 2, 4],
      explanation:
        'Parallelograms, rectangles, squares, and rhombi have two pairs of parallel opposite sides.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'A square has perimeter 36. Enter its area.',
      correctAnswer: '81',
      acceptedAnswers: ['81'],
      explanation: 'Each side is §36/4=9§, so area is §9^2=81§.',
    },
  ];

  const quickReference = [
    'Rectangle: §A = l \\times w§, §P = 2(l + w)§',
    'Square: §A = s²§, §P = 4s§',
    'Parallelogram: §A = b \\times h§',
    'Trapezoid: §A = ½(b₁ + b₂)h§',
    'Rhombus: §A = ½ \\times d₁ \\times d₂§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Quadrilateral concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Quadrilateral concepts for the GRE Quantitative section',
    aicontext:
      'Master rectangles, squares, parallelograms, trapezoids, and their properties and area formulas',
  };

  return (
    <LessonTemplate
      lessonId="quadrilaterals"
      lessonTitle="Quadrilaterals"
      lessonIcon={<FiSquare />}
      lessonDescription="Master squares, rectangles, parallelograms, trapezoids, and their properties"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="quadrilaterals" />
      }
    />
  );
};

export default QuadrilateralsLesson;
